(() => {
  'use strict';
  const integer = (value, minimum = 0) => Number.isSafeInteger(value) && value >= minimum;
  const variantId = value => /^\d+$/.test(String(value)) && Number(value) > 0;
  const key = (set, property, choice, value = '') => JSON.stringify([set, property, choice, value]);
  function plan({variant, quantity, properties, selections, expectedExtra, available, components, currency, complete, group, parentTitle}) {
    if (!complete || !/^[A-Z]{3}$/.test(currency) || !variantId(variant) || !integer(quantity, 1) || !integer(expectedExtra)) throw Error('The personalisation is not ready.');
    if (!group || !Array.isArray(selections)) throw Error('The personalisation is incomplete.');
    const parent = {id: String(variant), quantity, properties: {...properties, _orli_native_group: group, _orli_native_parent: 'true'}};
    const items = [parent];
    const title = typeof parentTitle === 'string' ? parentTitle.trim() : '';
    let extra = 0;
    for (const [index, selection] of selections.entries()) {
      const component = components[selection.key];
      if (!integer(selection.price, 1) || !integer(selection.units, 1)) throw Error('An option price is invalid.');
      if (!component || !variantId(component.variant)) throw Error('This charm is still being prepared.');
      const live = available[String(component.variant)];
      if (currency !== 'GBP' && (component.currency !== currency || live?.currency !== currency)) throw Error('The option currency has changed.');
      if (!live?.available || live.price !== selection.price || component.price !== selection.price) throw Error('This charm is not available at the selected price.');
      extra += selection.price * selection.units;
      const childProperties = title
        ? Object.fromEntries(Object.entries(selection.properties || {}).filter(([name, value]) => !Object.hasOwn(properties || {}, name) || properties[name] !== value))
        : {...selection.properties};
      if (title) childProperties['Add-on for'] = title;
      items.push({id: String(component.variant), quantity: quantity * selection.units, parent_id: String(variant),
        properties: {...childProperties, _orli_native_group: group, _orli_native_component: 'true', _orli_native_units: String(selection.units), _orli_native_selection: String(index + 1)}});
    }
    if (extra !== expectedExtra) throw Error('The selected options and total do not match.');
    return {items, extra, group};
  }
  function formData(result, original) {
    const body = new FormData();
    result.items.forEach((item, index) => {
      for (const field of ['id', 'quantity', 'parent_id']) if (item[field] !== undefined) body.append(`items[${index}][${field}]`, String(item[field]));
      for (const [name, value] of Object.entries(item.properties)) body.append(`items[${index}][properties][${name}]`, value);
    });
    const sellingPlan = original?.get('selling_plan');
    if (sellingPlan) body.append('items[0][selling_plan]', sellingPlan);
    return body;
  }
  function quantityUpdates(items, group, quantity) {
    if (!integer(quantity)) throw Error('The quantity is invalid.');
    const parent = items.filter(item => item.properties?._orli_native_group === group && item.properties?._orli_native_parent === 'true');
    if (parent.length !== 1) throw Error('The personalised item could not be found.');
    const updates = {[parent[0].key]: quantity};
    for (const item of items.filter(item => item.properties?._orli_native_group === group && item.properties?._orli_native_component === 'true')) {
      const units = Number(item.properties._orli_native_units);
      if (!integer(units, 1)) throw Error('A charm quantity is invalid.');
      updates[item.key] = quantity * units;
    }
    return updates;
  }
  function presentment({data, components, quotes, currency, material}) {
    const converted = JSON.parse(JSON.stringify(data)), mapped = {};
    const amount = identity => {
      const component = components[identity], quote = component && quotes[String(component.variant)];
      if (!component || !quote || !integer(quote.price) || quote.currency !== currency) throw Error('The option price could not be checked.');
      mapped[identity] = {...component, sourcePrice: component.price, price: quote.price, currency};
      return quote.price;
    };
    if (converted.set_id) {
      for (const control of converted.controls) for (const choice of control.choices) {
        if (!choice.child) {
          if (choice.price_pence) choice.price_pence = amount(key(converted.set_id, control.property, choice.id));
          continue;
        }
        const fees = choice.child.values.map(value => (choice.price_pence || 0) + (value.price_pence || 0)
          ? amount(key(converted.set_id, control.property, choice.id, value.id)) : 0);
        const base = Math.min(...fees);
        choice.price_pence = base;
        choice.child.values.forEach((value, index) => {value.price_pence = fees[index] - base;});
      }
    } else {
      for (const control of converted.controls) {
        if (control.option_price_pence) {
          if (control.price_mode !== 'per_letter') throw Error('The option charge could not be checked.');
          const prefix = JSON.stringify([control.source_set_id, control.property, control.id]).slice(0, -1) + ',';
          const identities = Object.keys(components).filter(identity => identity.startsWith(prefix) && JSON.parse(identity)[4] === material);
          const fees = new Set(identities.map(amount));
          if (fees.size !== 1) throw Error('The initial prices could not be checked.');
          control.option_price_pence = [...fees][0];
        }
        for (const value of control.values) {
          if (!value.price_pence) continue;
          const bar = control.source_set_id === '783151' && control.property === 'Engravable Bars';
          const parts = [control.source_set_id, control.property, value.id, ''];
          if (bar) parts.push(material);
          const unit = amount(JSON.stringify(parts));
          if (bar) {
            value.component_units = value.price_pence / 300;
            if (!integer(value.component_units, 1)) throw Error('The bar quantity could not be checked.');
            control.component_unit_price_pence = unit;
            value.price_pence = unit * value.component_units;
          } else value.price_pence = unit;
        }
      }
    }
    return {data: converted, components: mapped};
  }
  function availability({components, currency = 'GBP', request = window.fetch.bind(window), root = window.Shopify?.routes?.root || '/', now = Date.now} = {}) {
    const cache = new Map();
    async function product(handle, fresh) {
      if (!/^[a-z0-9-]+$/.test(handle)) throw Error('This option is still being prepared.');
      const previous = cache.get(handle);
      if (!fresh && previous && now() - previous.time < 30000) return previous.promise;
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 8000);
      const promise = request(`${root}products/${encodeURIComponent(handle)}.js`, {credentials: 'same-origin', signal: controller.signal})
        .then(response => {if (!response.ok) throw Error('This option is not available yet.');return response.json();})
        .then(data => {if (data.handle !== handle || !Array.isArray(data.variants)) throw Error('The option could not be checked.');return data;})
        .catch(error => {if (cache.get(handle)?.promise === promise) cache.delete(handle);throw error;})
        .finally(() => clearTimeout(timer));
      cache.set(handle, {time: now(), promise});return promise;
    }
    async function currencyCheck() {
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await request(`${root}cart.js`, {credentials: 'same-origin', signal: controller.signal});
        if (!response.ok || (await response.json()).currency !== currency) throw Error('The shopping currency has changed.');
      } finally {clearTimeout(timer);}
    }
    const check = async (selections, fresh = false) => {
      if (fresh) await currencyCheck();
      const selected = selections.map(selection => {
        const component = components[selection.key];
        if (!component || !variantId(component.variant) || component.price !== selection.price) throw Error('This option is still being prepared.');
        return component;
      });
      const handles = [...new Set(selected.map(component => component.handle))];
      const products = await Promise.all(handles.map(handle => product(handle, fresh)));
      const available = {};
      for (const component of selected) {
        const data = products[handles.indexOf(component.handle)];
        const variant = data.variants.find(item => String(item.id) === String(component.variant));
        if (!variant || !variant.available || variant.price !== component.price) throw Error('This option is not available at the selected price.');
        available[String(variant.id)] = {available: true, price: variant.price, currency};
      }
      return available;
    };
    check.catalogue = async () => {
      await currencyCheck();
      const handles = [...new Set(Object.values(components).map(component => component.handle))], products = new Map();
      let next = 0;
      await Promise.all(Array.from({length: Math.min(6, handles.length)}, async () => {
        while (next < handles.length) {
          const handle = handles[next++];
          products.set(handle, await product(handle, false));
        }
      }));
      const quotes = {};
      for (const component of Object.values(components)) {
        const variant = products.get(component.handle)?.variants.find(item => String(item.id) === String(component.variant));
        if (!variant || !integer(variant.price)) throw Error('The option price could not be checked.');
        quotes[String(variant.id)] = {price: variant.price, available: variant.available, currency};
      }
      return quotes;
    };
    return check;
  }
  window.OrliPersonalisationCommerce = Object.freeze({key, plan, formData, quantityUpdates, availability, presentment});
})();
